/**
 * Plus Code (Open Location Code) trong địa chỉ Google Maps, VD "XN 7 - …, 6QR9+WFW, Phước Hòa, Hồ Chí Minh".
 * Anh Tiến 22/10/2026 (phiếu 004-GH-221026): địa chỉ giao đúng nhưng ERP định vị sai vì OpenStreetMap không hiểu Plus Code,
 * bỏ mã đi rồi tìm theo "Phước Hòa" (ước lượng, có khi trúng xã khác). Mã đầy đủ (8 ký tự + "+") giải thẳng ra tọa độ;
 * mã rút gọn (4–6 ký tự + "+", Google bỏ 2–4 ký tự đầu) cần một điểm tham chiếu gần (≤ ~50 km) để khôi phục,
 * theo đúng thuật toán recoverNearest của Google (github.com/google/open-location-code). Sai số ô mã 10 ký tự ~ 14 m.
 */
const ALPHABET = '23456789CFGHJMPQRVWX';
const BASE = 20;
const PAIR_LEN = 10;
const GRID_ROWS = 5;
const GRID_COLS = 4;
const C = `[${ALPHABET}]`;
/** Mã trong chữ: ranh giới là đầu/cuối, khoảng trắng hoặc dấu phẩy. Chỉ nhận chữ in hoa (Google luôn in hoa) để tránh trùng chữ thường. */
const CODE_RE = new RegExp(`(?:^|[\\s,(])(${C}{2,8}\\+${C}{0,7})(?=$|[\\s,)])`);

export interface PlusCodeMatch {
  code: string;
  /** true = mã đầy đủ (8 ký tự trước "+"), giải được không cần tham chiếu */
  full: boolean;
  /** Phần chữ còn lại sau khi bỏ mã (để tìm điểm tham chiếu theo xã / tỉnh) */
  rest: string;
}

function valid(code: string): boolean {
  const sep = code.indexOf('+');
  if (sep < 2 || sep > 8 || sep % 2 === 1) return false;
  const after = code.length - sep - 1;
  if (after === 1) return false;
  // Mã đầy đủ ngắn hơn 8 phải đệm "0" – không gặp trong địa chỉ, không nhận
  if (sep === 8) return after === 0 || after >= 2;
  return after >= 2;
}

/** Tìm Plus Code đầu tiên trong địa chỉ; không có thì null. */
export function findPlusCode(text: string): PlusCodeMatch | null {
  const m = text.match(CODE_RE);
  if (!m || !valid(m[1]!)) return null;
  const code = m[1]!;
  const rest = text
    .replace(code, ' ')
    .split(',')
    .map((p) => p.trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .join(', ');
  return { code, full: code.indexOf('+') === 8, rest };
}

/** Tâm ô của mã đầy đủ. Mã không hợp lệ → null. */
export function decodePlusCode(full: string): { lat: number; lng: number } | null {
  const code = full.toUpperCase();
  if (!valid(code) || code.indexOf('+') !== 8) return null;
  const digits = code.replace('+', '');
  let lat = -90;
  let lng = -180;
  let latRes = 400;
  let lngRes = 400;
  for (let i = 0; i < Math.min(digits.length, PAIR_LEN); i += 2) {
    latRes /= BASE;
    lngRes /= BASE;
    lat += ALPHABET.indexOf(digits[i]!) * latRes;
    lng += ALPHABET.indexOf(digits[i + 1]!) * lngRes;
  }
  for (let i = PAIR_LEN; i < digits.length; i++) {
    const v = ALPHABET.indexOf(digits[i]!);
    latRes /= GRID_ROWS;
    lngRes /= GRID_COLS;
    lat += Math.floor(v / GRID_COLS) * latRes;
    lng += (v % GRID_COLS) * lngRes;
  }
  if (digits.length < PAIR_LEN) return null;
  return { lat: round6(lat + latRes / 2), lng: round6(normLng(lng + lngRes / 2)) };
}

/** Mã rút gọn + điểm tham chiếu → tọa độ (ô gần tham chiếu nhất). Mã đầy đủ thì giải thẳng. */
export function recoverPlusCode(code: string, refLat: number, refLng: number): { lat: number; lng: number } | null {
  const c = code.toUpperCase();
  if (!valid(c)) return null;
  const sep = c.indexOf('+');
  if (sep === 8) return decodePlusCode(c);
  const pad = 8 - sep;
  const resolution = Math.pow(BASE, 2 - pad / 2);
  const half = resolution / 2;
  const rLat = Math.min(90, Math.max(-90, refLat));
  const rLng = normLng(refLng);
  const p = decodePlusCode(encodePrefix(rLat, rLng).slice(0, pad) + c);
  if (!p) return null;
  let { lat, lng } = p;
  if (rLat + half < lat && lat - resolution >= -90) lat -= resolution;
  else if (rLat - half > lat && lat + resolution <= 90) lat += resolution;
  if (rLng + half < lng) lng -= resolution;
  else if (rLng - half > lng) lng += resolution;
  return { lat: round6(lat), lng: round6(normLng(lng)) };
}

/** 8 ký tự cặp đầu của tọa độ (đủ làm tiền tố khôi phục mã rút gọn) */
function encodePrefix(lat: number, lng: number): string {
  let la = Math.min(lat, 90 - 1e-9) + 90;
  let lo = normLng(lng) + 180;
  let res = 20;
  let out = '';
  for (let i = 0; i < 4; i++) {
    const a = Math.floor(la / res);
    const b = Math.floor(lo / res);
    out += ALPHABET[a]! + ALPHABET[b]!;
    la -= a * res;
    lo -= b * res;
    res /= BASE;
  }
  return out;
}

function normLng(lng: number): number {
  let x = lng;
  while (x < -180) x += 360;
  while (x >= 180) x -= 360;
  return x;
}
const round6 = (x: number) => Math.round(x * 1e6) / 1e6;
